import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    TouchSensor,
    closestCenter,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Paper,
    Skeleton,
    Stack,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    Add,
    ArrowBack,
    ArrowDownward,
    ArrowUpward,
    Close,
    CloudUpload,
    Delete,
    DragIndicator,
    LockOutlined,
    OpenInNew,
    StopCircleOutlined,
} from "@mui/icons-material";
import { Product } from "../../types";
import {
    CampaignDetail,
    CampaignSave,
    DiscountType,
    PriceState,
    campaignService,
    discountedPrice,
} from "../../services/campaignService";
import { ProductPickerDialog } from "../../components/products/ProductPickerDialog";
import { ProductThumbnail } from "../../components/common/ProductThumbnail";
import { STOREFRONT_HOST, campaignPageUrl } from "../../config/storefront";
import { SLUG_RE, cleanSlugInput, slugFromName } from "../../utils/slug";
import { formatCurrencyBDT } from "../../utils/currency";
import { STATUS_CHIP, fromNow } from "./CampaignList";

const taka = (n: number) => formatCurrencyBDT(n, { ignoreDecimals: true });
const BADGE_MAX = 24;

interface Row {
    product: Product;
    /** The campaign price as typed ("" = none: regular price, badge only). */
    price: string;
    state: PriceState;
    otherName: string | null;
    /** The product's own sale price, when on or coming — it stays, and
     * customers get the lower of it and the campaign price. */
    ownSale: number | null;
}

interface Form {
    name: string;
    slug: string;
    badge: string;
    description: string;
    startsAt: Date | null;
    endsAt: Date | null;
    showOnHome: boolean;
    discountType: DiscountType;
    discountValue: string;
}

const EMPTY_FORM: Form = {
    name: "",
    slug: "",
    badge: "",
    description: "",
    startsAt: new Date(),
    endsAt: null,
    showOnHome: true,
    discountType: "percent",
    discountValue: "",
};

function formFrom(c: CampaignDetail): Form {
    return {
        name: c.name,
        slug: c.slug,
        badge: c.badge_label,
        description: c.description ?? "",
        startsAt: new Date(c.starts_at),
        endsAt: c.ends_at ? new Date(c.ends_at) : null,
        showOnHome: c.show_on_home,
        // The quick fill (and default for products added later).
        discountType: c.discount_type === "none" ? "percent" : c.discount_type,
        discountValue: c.discount_value != null ? String(Number(c.discount_value)) : "",
    };
}

function rowsFrom(c: CampaignDetail): Row[] {
    return c.items.map((item) => ({
        product: item.product,
        price:
            item.price_state !== "other_campaign" && item.campaign_price != null
                ? String(Number(item.campaign_price))
                : "",
        state: item.price_state,
        otherName: item.other_campaign_name,
        ownSale: item.own_sale_price != null ? Number(item.own_sale_price) : null,
    }));
}

/** A product just picked: locked when another running campaign prices it.
 * (The server has the final word on save.) */
function stateOfPicked(p: Product, campaignId: string | undefined): Pick<Row, "state" | "otherName"> {
    const otherRunning =
        !!p.sale_campaign_id &&
        p.sale_campaign_id !== campaignId &&
        p.campaign_price != null &&
        (!p.campaign_price_ends_at || new Date(p.campaign_price_ends_at).getTime() > Date.now());
    return otherRunning
        ? { state: "other_campaign", otherName: p.live_campaign?.name ?? "another campaign" }
        : { state: "none", otherName: null };
}

/** The product's own sale price when it's on or still to come. */
function ownSaleOf(p: Product): number | null {
    if (p.sale_price == null || Number(p.sale_price) >= Number(p.selling_price)) return null;
    if (p.sale_ends_at && new Date(p.sale_ends_at).getTime() <= Date.now()) return null;
    return Number(p.sale_price);
}

function endOfMonth(from: Date): Date {
    const d = new Date(from.getFullYear(), from.getMonth() + 1, 0, 23, 59, 0, 0);
    return d;
}

function addDays(from: Date, days: number): Date {
    const d = new Date(from);
    d.setDate(d.getDate() + days);
    d.setHours(23, 59, 0, 0);
    return d;
}

const snapshot = (form: Form, rows: Row[]) =>
    JSON.stringify({
        ...form,
        startsAt: form.startsAt?.getTime() ?? null,
        endsAt: form.endsAt?.getTime() ?? null,
        rows: rows.map((r) => [r.product.product_id, r.price]),
    });

// ─── Product row ─────────────────────────────────────────────────────────────

const ProductRow: React.FC<{
    row: Row;
    index: number;
    count: number;
    onPrice: (price: string) => void;
    onMove: (to: number) => void;
    onRemove: () => void;
}> = ({ row, index, count, onPrice, onMove, onRemove }) => {
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
        id: row.product.product_id,
    });
    const p = row.product;
    const regular = Number(p.selling_price);
    const cost = Number(p.cost_price);
    const price = row.price === "" ? null : Number(row.price);
    const locked = row.state === "other_campaign";
    const stock = (p.stock?.quantity ?? 0) - (p.stock?.reserved_quantity ?? 0);
    const priceOk = price != null && price > 0 && price < regular;

    let priceNote: React.ReactNode;
    if (locked) priceNote = null;
    else if (price == null)
        priceNote = (
            <Typography variant="caption" color="text.secondary">
                Regular price
            </Typography>
        );
    else if (!(price > 0)) priceNote = <Typography variant="caption" color="error">Enter a price</Typography>;
    else if (price >= regular) priceNote = <Typography variant="caption" color="error">Must be below {taka(regular)}</Typography>;
    else
        priceNote = (
            <Typography variant="caption" color={price < cost ? "error" : "success.main"} fontWeight={600}>
                −{Math.round((1 - price / regular) * 100)}%{price < cost ? ` · below cost ${taka(cost)}` : ""}
            </Typography>
        );

    // How its own sale and the campaign price meet.
    let ownNote: { label: string; color: "default" | "warning" } | null = null;
    if (row.ownSale != null && !locked) {
        if (priceOk && row.ownSale < price!)
            ownNote = { label: `Own sale ${taka(row.ownSale)} is lower — customers get ${taka(row.ownSale)}`, color: "warning" };
        else if (priceOk) ownNote = { label: `Own sale ${taka(row.ownSale)} returns after the campaign`, color: "default" };
        else ownNote = { label: `Own sale ${taka(row.ownSale)} continues`, color: "default" };
    }

    return (
        <Paper
            ref={setNodeRef}
            variant="outlined"
            sx={{
                p: 1,
                pr: 1.5,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                flexWrap: { xs: "wrap", md: "nowrap" },
                transform: CSS.Transform.toString(transform),
                transition,
                position: "relative",
                zIndex: isDragging ? 2 : undefined,
                boxShadow: isDragging ? 6 : 0,
                bgcolor: "background.paper",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <IconButton
                    ref={setActivatorNodeRef}
                    size="small"
                    aria-label={`Drag ${p.name}`}
                    sx={{ cursor: "grab", touchAction: "none" }}
                    {...attributes}
                    {...listeners}
                >
                    <DragIndicator fontSize="small" />
                </IconButton>
                <Typography variant="caption" color="text.secondary" sx={{ width: 22, textAlign: "center", fontVariantNumeric: "tabular-nums" }}>
                    {index + 1}
                </Typography>
                <ProductThumbnail images={p.images} size={44} />
            </Box>

            <Box sx={{ flex: 1, minWidth: { xs: "calc(100% - 140px)", md: 0 } }}>
                <Typography variant="body2" fontWeight={600} noWrap title={p.name}>
                    {p.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div" noWrap>
                    {p.sku} · {stock > 0 ? `${stock} in stock` : "Out of stock"} · regular {taka(regular)} · cost {taka(cost)}
                </Typography>
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.25 }}>
                    {locked && (
                        <Chip size="small" icon={<LockOutlined />} label={`Has ${row.otherName}'s price — badge only here`} />
                    )}
                    {ownNote && <Chip size="small" variant="outlined" color={ownNote.color} label={ownNote.label} />}
                    {!p.is_visible && <Chip size="small" variant="outlined" label="Hidden from website" />}
                </Box>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", minWidth: 128, ml: { xs: "auto", md: 0 } }}>
                {locked ? (
                    <Typography variant="body2" color="text.secondary">—</Typography>
                ) : (
                    <>
                        <TextField
                            size="small"
                            type="number"
                            value={row.price}
                            placeholder={String(regular)}
                            onChange={(e) => onPrice(e.target.value)}
                            inputProps={{ min: 1, step: 1, "aria-label": `Campaign price for ${p.name}` }}
                            InputProps={{ startAdornment: <InputAdornment position="start">৳</InputAdornment> }}
                            sx={{ width: 128 }}
                        />
                        {priceNote}
                    </>
                )}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center" }}>
                <Tooltip title="Move up">
                    <span>
                        <IconButton size="small" disabled={index === 0} onClick={() => onMove(index - 1)} aria-label="Move up">
                            <ArrowUpward fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Move down">
                    <span>
                        <IconButton size="small" disabled={index === count - 1} onClick={() => onMove(index + 1)} aria-label="Move down">
                            <ArrowDownward fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                {!locked && row.price !== "" && (
                    <Tooltip title="Clear the campaign price (regular price, badge only)">
                        <IconButton size="small" onClick={() => onPrice("")} aria-label="Clear campaign price">
                            <Close fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                <Tooltip title="Remove from campaign">
                    <IconButton size="small" color="error" onClick={onRemove} aria-label="Remove from campaign">
                        <Delete fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
        </Paper>
    );
};

// ─── Page ────────────────────────────────────────────────────────────────────

const card = { p: { xs: 1.75, sm: 2.5 }, borderRadius: 2, mb: 2 } as const;

export const CampaignEditor: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const isNew = !id || id === "new";
    const navigate = useNavigate();

    const [campaign, setCampaign] = useState<CampaignDetail | null>(null);
    const [form, setForm] = useState<Form>(EMPTY_FORM);
    const [rows, setRows] = useState<Row[]>([]);
    const [savedSnapshot, setSavedSnapshot] = useState(() => snapshot(EMPTY_FORM, []));
    const [slugEdited, setSlugEdited] = useState(false);
    const [badgeEdited, setBadgeEdited] = useState(false);

    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [pickerOpen, setPickerOpen] = useState(false);
    const [confirm, setConfirm] = useState<"end" | "delete" | null>(null);
    const [bannerBusy, setBannerBusy] = useState(false);
    const bannerInput = useRef<HTMLInputElement>(null);

    const applyCampaign = useCallback((c: CampaignDetail) => {
        const f = formFrom(c);
        const r = rowsFrom(c);
        setCampaign(c);
        setForm(f);
        setRows(r);
        setSavedSnapshot(snapshot(f, r));
        setSlugEdited(true);
        setBadgeEdited(true);
    }, []);

    useEffect(() => {
        if (isNew) return;
        setLoading(true);
        campaignService
            .get(id!)
            .then(applyCampaign)
            .catch((err) => setError(err?.response?.data?.detail || "Couldn't load this campaign."))
            .finally(() => setLoading(false));
    }, [id, isNew, applyCampaign]);

    const dirty = snapshot(form, rows) !== savedSnapshot;
    useEffect(() => {
        if (!dirty) return;
        const warn = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", warn);
        return () => window.removeEventListener("beforeunload", warn);
    }, [dirty]);

    const set = <K extends keyof Form>(key: K, value: Form[K]) => setForm((f) => ({ ...f, [key]: value }));
    // The quick fill: "% off" or "৳ off" filled into prices, then editable.
    // Saved as the default for products added later (bulk add).
    const fillNumber = form.discountValue === "" ? null : Number(form.discountValue);
    const fillValid =
        fillNumber != null &&
        (form.discountType === "percent" ? fillNumber >= 1 && fillNumber <= 99 : fillNumber > 0);
    const filled = (p: Product) =>
        fillValid ? discountedPrice(Number(p.selling_price), form.discountType, fillNumber) : null;

    // ── Products ─────────────────────────────────────────────────────────────
    const inCampaign = useMemo(() => new Set(rows.map((r) => r.product.product_id)), [rows]);

    const addProducts = (products: Product[]) => {
        setNotice("");
        setRows((prev) => [
            ...prev,
            ...products
                .filter((p) => !prev.some((r) => r.product.product_id === p.product_id))
                .map((p) => {
                    const st = stateOfPicked(p, campaign?.campaign_id);
                    const price = st.state === "other_campaign" ? null : filled(p);
                    return { product: p, price: price != null ? String(price) : "", ...st, ownSale: ownSaleOf(p) };
                }),
        ]);
    };

    const setRowPrice = (index: number, price: string) =>
        setRows((prev) => prev.map((r, i) => (i === index ? { ...r, price } : r)));

    /** Fill prices from the quick fill — every product, or only those
     * without a price yet. Locked ones are left alone. */
    const fillPrices = (onlyEmpty: boolean) =>
        setRows((prev) =>
            prev.map((r) => {
                if (r.state === "other_campaign" || (onlyEmpty && r.price !== "")) return r;
                const price = filled(r.product);
                return price != null ? { ...r, price: String(price) } : r;
            }),
        );

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );
    const onDragEnd = (e: DragEndEvent) => {
        if (!e.over || e.active.id === e.over.id) return;
        const from = rows.findIndex((r) => r.product.product_id === e.active.id);
        const to = rows.findIndex((r) => r.product.product_id === e.over!.id);
        if (from >= 0 && to >= 0) setRows((prev) => arrayMove(prev, from, to));
    };

    // ── Validation + save ────────────────────────────────────────────────────
    const problems = useMemo(() => {
        const list: string[] = [];
        if (!form.name.trim()) list.push("Give the campaign a name.");
        if (form.badge.length > BADGE_MAX) list.push(`Keep the badge to ${BADGE_MAX} characters.`);
        if (form.slug && !SLUG_RE.test(form.slug)) list.push("The web address can only use lowercase letters, numbers and single hyphens.");
        if (!form.startsAt || isNaN(form.startsAt.getTime())) list.push("Choose when it starts.");
        if (form.endsAt && form.startsAt && form.endsAt <= form.startsAt) list.push("The end must be after the start.");
        if (form.discountValue !== "" && !fillValid)
            list.push(
                form.discountType === "percent"
                    ? "The quick-fill percentage must be between 1 and 99."
                    : "The quick-fill amount must be more than 0.",
            );
        const bad = rows.filter((r) => {
            if (r.price === "" || r.state === "other_campaign") return false;
            const v = Number(r.price);
            return !(v > 0) || v >= Number(r.product.selling_price);
        });
        if (bad.length) list.push(`Check the campaign price of ${bad.length} product${bad.length === 1 ? "" : "s"}.`);
        return list;
    }, [form, rows, fillValid]);

    const save = async () => {
        if (problems.length) {
            setError(problems[0]);
            return;
        }
        const body: CampaignSave = {
            name: form.name.trim(),
            slug: form.slug || undefined,
            badge_label: form.badge.trim() || undefined,
            description: form.description.trim() || null,
            starts_at: form.startsAt!.toISOString(),
            ends_at: form.endsAt ? form.endsAt.toISOString() : null,
            show_on_home: form.showOnHome,
            discount_type: fillValid ? form.discountType : "none",
            discount_value: fillValid ? fillNumber : null,
            products: rows.map((r) => ({
                product_id: r.product.product_id,
                campaign_price: r.state !== "other_campaign" && r.price !== "" ? Number(r.price) : null,
            })),
        };
        setSaving(true);
        setError("");
        setNotice("");
        try {
            if (isNew) {
                const created = await campaignService.create(body);
                applyCampaign(created);
                navigate(`/campaigns/${created.campaign_id}`, { replace: true });
                setNotice(
                    created.status === "live"
                        ? "Campaign created — it's live on the website now (lists refresh within a minute)."
                        : `Campaign created — it goes live ${fromNow(created.starts_at)}.`,
                );
            } else {
                applyCampaign(await campaignService.update(id!, body));
                setNotice("Saved. The website shows the changes within a minute.");
            }
        } catch (err: any) {
            setError(err?.response?.data?.detail || "Couldn't save. Your changes are still here — try again.");
        } finally {
            setSaving(false);
        }
    };

    const discard = () => {
        if (campaign) applyCampaign(campaign);
        else {
            setForm(EMPTY_FORM);
            setRows([]);
            setSlugEdited(false);
            setBadgeEdited(false);
        }
        setError("");
    };

    const runConfirm = async () => {
        if (!campaign || !confirm) return;
        setSaving(true);
        try {
            if (confirm === "end") {
                applyCampaign(await campaignService.endNow(campaign.campaign_id));
                setNotice("Ended. Its products are back to their regular prices.");
            } else {
                await campaignService.remove(campaign.campaign_id);
                navigate("/campaigns", { replace: true });
                return;
            }
        } catch (err: any) {
            setError(err?.response?.data?.detail || "That didn't work — try again.");
        } finally {
            setSaving(false);
            setConfirm(null);
        }
    };

    const uploadBanner = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !campaign) return;
        setBannerBusy(true);
        setError("");
        try {
            const updated = await campaignService.uploadBanner(campaign.campaign_id, file);
            setCampaign((c) => (c ? { ...c, banner_image_url: updated.banner_image_url } : c));
        } catch (err: any) {
            setError(err?.response?.data?.detail || "Couldn't upload the banner.");
        } finally {
            setBannerBusy(false);
            if (bannerInput.current) bannerInput.current.value = "";
        }
    };

    const removeBanner = async () => {
        if (!campaign || !window.confirm("Remove the banner from this campaign's page?")) return;
        setBannerBusy(true);
        try {
            await campaignService.deleteBanner(campaign.campaign_id);
            setCampaign((c) => (c ? { ...c, banner_image_url: null } : c));
        } catch (err: any) {
            setError(err?.response?.data?.detail || "Couldn't remove the banner.");
        } finally {
            setBannerBusy(false);
        }
    };

    const goBack = () => {
        if (dirty && !window.confirm("Leave without saving your changes?")) return;
        navigate("/campaigns");
    };

    // What the dates mean, in words.
    const timing = (() => {
        const now = Date.now();
        const s = form.startsAt?.getTime();
        const e = form.endsAt?.getTime();
        if (!s || isNaN(s)) return "";
        if (e && e <= now) return "These dates are in the past — it will show as ended.";
        if (s > now) return `Goes live ${fromNow(form.startsAt!.toISOString())}${e ? `, ends ${fromNow(form.endsAt!.toISOString())}` : ", and runs until you end it"}.`;
        return e ? `Live now — ends ${fromNow(form.endsAt!.toISOString())}.` : "Live now — runs until you end it.";
    })();

    const pricedCount = rows.filter((r) => r.price !== "" && r.state !== "other_campaign").length;
    // The biggest discount the prices give — for the badge preview.
    const maxPercent = rows.reduce((best, r) => {
        const v = Number(r.price);
        const reg = Number(r.product.selling_price);
        if (r.price === "" || r.state === "other_campaign" || !(v > 0) || v >= reg) return best;
        return Math.max(best, Math.round((1 - v / reg) * 100));
    }, 0);
    const status = campaign?.status;

    if (loading) {
        return (
            <Box>
                <Skeleton width={240} height={48} />
                <Skeleton variant="rounded" height={220} sx={{ mb: 2 }} />
                <Skeleton variant="rounded" height={320} />
            </Box>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ maxWidth: 1100, pb: dirty ? 10 : 2 }}>
                {/* Header */}
                <Button startIcon={<ArrowBack />} onClick={goBack} sx={{ mb: 1, ml: -1 }}>
                    Campaigns
                </Button>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
                    <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: "1.6rem", sm: "2.125rem" }, minWidth: 0 }}>
                        {isNew ? "New campaign" : form.name || campaign?.name}
                    </Typography>
                    {status && <Chip label={STATUS_CHIP[status].label} color={STATUS_CHIP[status].color} />}
                    <Box sx={{ flex: 1 }} />
                    {campaign && (
                        <Button
                            variant="outlined"
                            startIcon={<OpenInNew />}
                            href={campaignPageUrl(campaign.slug)}
                            target="_blank"
                            rel="noopener"
                        >
                            View on website
                        </Button>
                    )}
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                        {error}
                    </Alert>
                )}
                {notice && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice("")}>
                        {notice}
                    </Alert>
                )}

                {/* Details */}
                <Paper variant="outlined" sx={card}>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
                        Details
                    </Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
                        <TextField
                            label="Name"
                            required
                            value={form.name}
                            onChange={(e) => {
                                const name = e.target.value;
                                setForm((f) => ({
                                    ...f,
                                    name,
                                    slug: !slugEdited ? slugFromName(name) : f.slug,
                                    badge: !badgeEdited ? name.slice(0, BADGE_MAX) : f.badge,
                                }));
                            }}
                            placeholder="e.g. Eid Offer"
                            helperText="The heading on its page and homepage section"
                        />
                        <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                            <TextField
                                label="Badge"
                                value={form.badge}
                                onChange={(e) => {
                                    setBadgeEdited(true);
                                    set("badge", e.target.value);
                                }}
                                error={form.badge.length > BADGE_MAX}
                                helperText={`On its products' photos · ${form.badge.length}/${BADGE_MAX}`}
                                sx={{ flex: 1 }}
                            />
                            {/* What the badge looks like on a product */}
                            <Box
                                aria-hidden
                                sx={{
                                    width: 96,
                                    height: 72,
                                    flexShrink: 0,
                                    borderRadius: 2,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    bgcolor: "grey.50",
                                    p: 0.75,
                                    display: "flex",
                                    flexWrap: "wrap",
                                    alignContent: "flex-start",
                                    gap: 0.5,
                                }}
                            >
                                <Box
                                    sx={{
                                        maxWidth: "100%",
                                        px: 0.75,
                                        py: 0.5,
                                        borderRadius: 1.5,
                                        bgcolor: "#b4471c",
                                        color: "#fff",
                                        fontSize: 10,
                                        fontWeight: 700,
                                        lineHeight: 1,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                    }}
                                >
                                    {form.badge || form.name || "Badge"}
                                </Box>
                                {maxPercent > 0 ? (
                                    <Box
                                        sx={{
                                            px: 0.5,
                                            py: 0.4,
                                            borderRadius: 1.5,
                                            border: "1.5px solid #b4471c",
                                            color: "#b4471c",
                                            bgcolor: "#fff",
                                            fontSize: 10,
                                            fontWeight: 700,
                                            lineHeight: 1,
                                        }}
                                    >
                                        −{maxPercent}%
                                    </Box>
                                ) : null}
                            </Box>
                        </Box>
                        <TextField
                            label="Web address"
                            value={form.slug}
                            onChange={(e) => {
                                setSlugEdited(true);
                                set("slug", cleanSlugInput(e.target.value));
                            }}
                            error={!!form.slug && !SLUG_RE.test(form.slug)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start" sx={{ mr: 0 }}>
                                        <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: { xs: 150, sm: "none" } }}>
                                            {STOREFRONT_HOST}/offers/
                                        </Typography>
                                    </InputAdornment>
                                ),
                            }}
                            helperText={
                                campaign && form.slug !== campaign.slug
                                    ? "Changing it breaks links you've already shared or used in ads."
                                    : "Use this link in ads and posts"
                            }
                            FormHelperTextProps={{
                                sx: { color: campaign && form.slug !== campaign.slug ? "warning.main" : undefined },
                            }}
                        />
                        <FormControlLabel
                            control={<Switch checked={form.showOnHome} onChange={(e) => set("showOnHome", e.target.checked)} />}
                            label={
                                <Box>
                                    <Typography variant="body2" fontWeight={600}>
                                        Show on the homepage
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        A section with its products while it's live
                                    </Typography>
                                </Box>
                            }
                            sx={{ alignItems: "flex-start", m: 0, "& .MuiSwitch-root": { mt: -0.5 } }}
                        />
                        <TextField
                            label="Description (optional)"
                            value={form.description}
                            onChange={(e) => set("description", e.target.value)}
                            multiline
                            minRows={3}
                            helperText="Shown under the name on its page. **Bold** and line breaks work."
                            sx={{ gridColumn: { md: "1 / -1" } }}
                        />
                    </Box>
                </Paper>

                {/* Dates */}
                <Paper variant="outlined" sx={card}>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
                        Dates
                    </Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
                        <Box>
                            <DateTimePicker
                                label="Starts"
                                value={form.startsAt}
                                onChange={(v) => set("startsAt", v)}
                                slotProps={{ textField: { fullWidth: true } }}
                            />
                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                <Chip size="small" label="Now" onClick={() => set("startsAt", new Date())} />
                            </Stack>
                        </Box>
                        <Box>
                            <DateTimePicker
                                label="Ends"
                                value={form.endsAt}
                                onChange={(v) => set("endsAt", v)}
                                slotProps={{
                                    textField: { fullWidth: true, helperText: form.endsAt ? undefined : "No end — runs until you end it" },
                                    actionBar: { actions: ["clear", "accept"] },
                                }}
                            />
                            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", rowGap: 1 }}>
                                {[3, 7].map((d) => (
                                    <Chip
                                        key={d}
                                        size="small"
                                        label={`${d} days`}
                                        onClick={() => set("endsAt", addDays(form.startsAt ?? new Date(), d))}
                                    />
                                ))}
                                <Chip size="small" label="End of month" onClick={() => set("endsAt", endOfMonth(form.startsAt ?? new Date()))} />
                                <Chip size="small" variant="outlined" label="No end" onClick={() => set("endsAt", null)} />
                            </Stack>
                        </Box>
                    </Box>
                    {timing && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                            {timing} Prices switch on and off by themselves.
                        </Typography>
                    )}
                </Paper>

                {/* Banner */}
                <Paper variant="outlined" sx={card}>
                    <Typography variant="h6" fontWeight={700}>
                        Banner
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        The wide picture at the top of its page and homepage section. About 1920×480 works best — phones
                        show only the unshaded middle.
                    </Typography>
                    {!campaign ? (
                        <Typography variant="body2" color="text.secondary">
                            Save the campaign first, then add its banner here. Without one, the page shows a warm band
                            with the discount.
                        </Typography>
                    ) : (
                        <>
                            <Box
                                sx={{
                                    position: "relative",
                                    width: "100%",
                                    aspectRatio: "4 / 1",
                                    borderRadius: 1.5,
                                    overflow: "hidden",
                                    border: "1px solid",
                                    borderColor: "divider",
                                    bgcolor: "action.hover",
                                }}
                            >
                                {campaign.banner_image_url ? (
                                    <>
                                        <Box
                                            component="img"
                                            src={campaign.banner_image_url}
                                            alt="Campaign banner"
                                            sx={{ display: "block", width: "100%", height: "100%", objectFit: "cover" }}
                                        />
                                        <Box aria-hidden sx={{ position: "absolute", inset: 0, display: "flex", pointerEvents: "none" }}>
                                            <Box sx={{ width: "17.5%", bgcolor: "rgba(0,0,0,0.38)" }} />
                                            <Box
                                                sx={{
                                                    flex: 1,
                                                    borderLeft: "1px dashed rgba(255,255,255,0.85)",
                                                    borderRight: "1px dashed rgba(255,255,255,0.85)",
                                                }}
                                            />
                                            <Box sx={{ width: "17.5%", bgcolor: "rgba(0,0,0,0.38)" }} />
                                        </Box>
                                    </>
                                ) : (
                                    <Box
                                        sx={{
                                            height: "100%",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            background: "linear-gradient(135deg, #b4471c, #e8833f)",
                                            color: "#fff",
                                            fontWeight: 700,
                                            fontSize: { xs: 16, sm: 24 },
                                        }}
                                    >
                                        No banner yet
                                    </Box>
                                )}
                                {bannerBusy && (
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            inset: 0,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            bgcolor: "rgba(255,255,255,0.6)",
                                        }}
                                    >
                                        <CircularProgress size={28} />
                                    </Box>
                                )}
                            </Box>
                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<CloudUpload />}
                                    onClick={() => bannerInput.current?.click()}
                                    disabled={bannerBusy}
                                >
                                    {campaign.banner_image_url ? "Replace banner" : "Upload banner"}
                                </Button>
                                {campaign.banner_image_url && (
                                    <Button size="small" color="error" onClick={removeBanner} disabled={bannerBusy}>
                                        Remove
                                    </Button>
                                )}
                                <input ref={bannerInput} type="file" accept="image/*" hidden onChange={uploadBanner} />
                            </Stack>
                        </>
                    )}
                </Paper>

                {/* Products and their campaign prices */}
                <Paper variant="outlined" sx={card}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1 }}>
                        <Box sx={{ flex: 1, minWidth: 200 }}>
                            <Typography variant="h6" fontWeight={700}>
                                Products & prices ({rows.length})
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Set each product's campaign price — leave it blank to keep the regular price (it still
                                gets the badge). A product's own sale stays: customers get whichever is lower, and it
                                carries on after the campaign. Drag to reorder.
                            </Typography>
                        </Box>
                        <Button variant="contained" startIcon={<Add />} onClick={() => setPickerOpen(true)}>
                            Add products
                        </Button>
                    </Box>

                    {/* Quick fill */}
                    <Box
                        sx={{
                            display: "flex",
                            gap: 1,
                            alignItems: "center",
                            flexWrap: "wrap",
                            p: 1.25,
                            mb: 1.5,
                            borderRadius: 1.5,
                            bgcolor: "action.hover",
                        }}
                    >
                        <Typography variant="body2" fontWeight={600} sx={{ mr: 0.5 }}>
                            Quick fill
                        </Typography>
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={form.discountType === "none" ? "percent" : form.discountType}
                            onChange={(_, v: DiscountType | null) => v && set("discountType", v)}
                            sx={{ bgcolor: "background.paper" }}
                        >
                            <ToggleButton value="percent">% off</ToggleButton>
                            <ToggleButton value="flat">৳ off</ToggleButton>
                        </ToggleButtonGroup>
                        <TextField
                            size="small"
                            type="number"
                            placeholder={form.discountType === "flat" ? "e.g. 30" : "e.g. 10"}
                            value={form.discountValue}
                            onChange={(e) => set("discountValue", e.target.value)}
                            error={form.discountValue !== "" && !fillValid}
                            inputProps={{
                                min: 1,
                                max: form.discountType === "percent" ? 99 : undefined,
                                "aria-label": form.discountType === "percent" ? "Percent off" : "Taka off",
                            }}
                            InputProps={{
                                [form.discountType === "flat" ? "startAdornment" : "endAdornment"]: (
                                    <InputAdornment position={form.discountType === "flat" ? "start" : "end"}>
                                        {form.discountType === "flat" ? "৳" : "%"}
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ width: 120, bgcolor: "background.paper" }}
                        />
                        <Button size="small" variant="outlined" disabled={!fillValid || rows.length === 0} onClick={() => fillPrices(false)}>
                            Fill all
                        </Button>
                        <Button
                            size="small"
                            disabled={!fillValid || !rows.some((r) => r.price === "" && r.state !== "other_campaign")}
                            onClick={() => fillPrices(true)}
                        >
                            Fill empty only
                        </Button>
                        <Typography variant="caption" color="text.secondary" sx={{ flexBasis: "100%" }}>
                            {fillValid
                                ? "Products you add later start at this discount too — every price stays editable."
                                : "Optional: fill many prices at once, then adjust any of them."}
                            {rows.length > 0 && ` ${pricedCount} of ${rows.length} have a campaign price.`}
                        </Typography>
                    </Box>

                    {rows.length === 0 ? (
                        <Box
                            sx={{
                                p: 3,
                                textAlign: "center",
                                border: "1px dashed",
                                borderColor: "divider",
                                borderRadius: 2,
                                color: "text.secondary",
                            }}
                        >
                            <Typography variant="body2">
                                No products yet. <b>Add products</b> to pick them by name, category or brand, then
                                set their campaign prices.
                            </Typography>
                        </Box>
                    ) : (
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                            <SortableContext items={rows.map((r) => r.product.product_id)} strategy={verticalListSortingStrategy}>
                                <Stack spacing={1}>
                                    {rows.map((row, i) => (
                                        <ProductRow
                                            key={row.product.product_id}
                                            row={row}
                                            index={i}
                                            count={rows.length}
                                            onPrice={(price) => setRowPrice(i, price)}
                                            onMove={(to) => setRows((prev) => arrayMove(prev, i, to))}
                                            onRemove={() => setRows((prev) => prev.filter((_, j) => j !== i))}
                                        />
                                    ))}
                                </Stack>
                            </SortableContext>
                        </DndContext>
                    )}
                </Paper>

                {/* End / delete */}
                {campaign && (
                    <Paper variant="outlined" sx={{ ...card, borderColor: "error.light" }}>
                        <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                            End or delete
                        </Typography>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                            {campaign.status !== "ended" && (
                                <Button
                                    variant="outlined"
                                    color="warning"
                                    startIcon={<StopCircleOutlined />}
                                    onClick={() => setConfirm("end")}
                                    disabled={saving || dirty}
                                >
                                    End now
                                </Button>
                            )}
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<Delete />}
                                onClick={() => setConfirm("delete")}
                                disabled={saving}
                            >
                                Delete campaign
                            </Button>
                        </Stack>
                        {dirty && campaign.status !== "ended" && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                                Save or discard your changes first to end it.
                            </Typography>
                        )}
                    </Paper>
                )}

                {/* Save bar */}
                {(dirty || isNew) && (
                    <Paper
                        elevation={8}
                        sx={{
                            position: "fixed",
                            left: { xs: 8, md: "auto" },
                            right: { xs: 8, md: 24 },
                            bottom: { xs: 8, md: 24 },
                            zIndex: (t) => t.zIndex.appBar,
                            borderRadius: 3,
                            px: 2,
                            py: 1.25,
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                        }}
                    >
                        <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                            {isNew ? "New campaign" : "Unsaved changes"}
                        </Typography>
                        {!isNew && (
                            <Button color="inherit" onClick={discard} disabled={saving}>
                                Discard
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            onClick={save}
                            disabled={saving}
                            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
                        >
                            {isNew ? "Create campaign" : "Save"}
                        </Button>
                    </Paper>
                )}

                <ProductPickerDialog
                    open={pickerOpen}
                    onClose={() => setPickerOpen(false)}
                    onAdd={addProducts}
                    excludeIds={inCampaign}
                    title="Add products to the campaign"
                />

                <Dialog open={!!confirm} onClose={() => !saving && setConfirm(null)}>
                    <DialogTitle>{confirm === "end" ? "End this campaign now?" : "Delete this campaign?"}</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            {confirm === "end"
                                ? "Its products go back to their regular prices straight away, and its page says the offer has ended. To run it again later, set new dates."
                                : "Its page stops working and the products it priced go back to their regular prices. This can't be undone."}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConfirm(null)} disabled={saving}>
                            Cancel
                        </Button>
                        <Button color="error" variant="contained" onClick={runConfirm} disabled={saving}>
                            {confirm === "end" ? "End now" : "Delete"}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </LocalizationProvider>
    );
};

export default CampaignEditor;
